import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { broadcasts } from '@repletoryreach/sdk-api';
import { Button, Card, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../../../../i18n/I18nProvider';
import { formatDateTime, formatCount, type Indexed } from '../../../../lib/format';
import { RecipientStatusBadge, reasonText } from '../../components/delivery';
import { useMetaBilling } from '../../../../lib/meta-billing';

const PAGE_SIZE = 50;

/**
 * The status chips, failures first. Read is WhatsApp's blue ticks and only offered there; people who read it are
 * counted under Read and not under Delivered, so on WhatsApp the Delivered chip says "Delivered, not read".
 */
const STATUSES = ['failed', 'filtered_opted_out', 'read', 'delivered', 'sent', 'queued', 'cancelled'] as const;

/**
 * Who a broadcast reached, and who it did not.
 *
 * `broadcasts-view-id.md` §4 item 4: Overview said "12 failed", Stats said
 * `failed · 12 · 3%`, and there was nowhere to go from there. No recipient
 * list, no error codes, no export. The review called this "the single
 * highest-value addition to the broadcasts surface, and the only way a user
 * can ever answer 'why didn't Thabo get it?'", and noted it was blocked on a
 * backend endpoint that did not exist. `GET /v1/broadcasts/{id}/recipients`
 * was added for it.
 */
export function RecipientsTab({ broadcastId, channel, failedCount, status: chosen, onStatus, poll }: {
  broadcastId: string;
  channel: broadcasts.BroadcastChannel;
  failedCount: number;
  /** The chip in the address (`?status=`), so the report can link straight to a filtered list. Null for the default. */
  status: string | null;
  onStatus: (status: string) => void;
  /** How often the page is asking again: reports keep changing rows after the broadcast finished. */
  poll: number | false;
}) {
  const t = useT();
  // Broadcasts on the business's own WhatsApp account explain a billing refusal as theirs to fix.
  const payer = useMetaBilling().onlyOwnAccount ? 'customer' as const : undefined;
  const whatsapp = channel === 'whatsapp';
  // Failures are what anyone opens this tab for, so that is where it starts
  // when there are any.
  const status = chosen ?? (failedCount > 0 ? 'failed' : '');
  const setStatus = onStatus;
  const [page, setPage] = useState(1);

  const { data, isPending, error } = useQuery({
    queryKey: ['broadcast', broadcastId, 'recipients', status, page],
    queryFn: () => broadcasts.listBroadcastRecipients(broadcastId, {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      ...(status ? { status } : {})
    }),
    placeholderData: (prev) => prev,
    // It never asked again, so a recipient who read the message an hour later still showed as sent (4 October 2026).
    refetchInterval: poll
  });

  useEffect(() => { setPage(1); }, [status]);

  const rows = (data?.data ?? []) as Indexed<broadcasts.BroadcastRecipient>[];
  const total = data?.meta?.total ?? 0;
  /** An email broadcast's roll is addresses, so the column says so. */
  const byEmail = rows.some((r) => !r.contact_msisdn && !!r.contact_email);

  return (
    <Stack gap="sm">
      <Group gap={6} wrap="wrap" role="tablist" aria-label={t('common.status')}>
        <button
          type="button"
          role="tab"
          aria-selected={status === ''}
          className={`rr-broadcasts__chip${status === '' ? ' is-active' : ''}`}
          onClick={() => setStatus('')}
        >
          {t('common.all')}
        </button>
        {STATUSES.filter((s) => whatsapp || s !== 'read').map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={status === s}
            className={`rr-broadcasts__chip${status === s ? ' is-active' : ''}`}
            onClick={() => setStatus(s)}
          >
            {s === 'delivered' && whatsapp ? t('broadcasts.recipient.deliveredNotRead') : t(`broadcasts.recipient.${s}`)}
          </button>
        ))}
      </Group>
      {whatsapp && (status === 'delivered' || status === 'read') && (
        <Text role="caption" c="var(--rr-color-text-muted)">{t('broadcasts.view.recipients.readNote')}</Text>
      )}

      {error && <Alert color="error" role="alert">{t('broadcasts.view.recipients.error')}</Alert>}

      {!isPending && rows.length === 0 ? (
        <Card withBorder>
          <EmptyState
            title={status ? t('broadcasts.view.recipients.emptyFiltered') : t('broadcasts.view.recipients.empty')}
            description={status === 'failed' ? t('broadcasts.view.recipients.noFailures') : undefined}
            action={status ? <Button size="xs" variant="default" onClick={() => setStatus('')}>{t('inbox.filters.clear')}</Button> : undefined}
          />
        </Card>
      ) : (
        <Card withBorder noPadding>
          <DataTable<Indexed<broadcasts.BroadcastRecipient>>
            records={rows}
            loading={isPending}
            idAccessor="id"
            page={page}
            totalRecords={total}
            recordsPerPage={PAGE_SIZE}
            onPageChange={setPage}
            emptyState={t('broadcasts.view.recipients.empty')}
            columns={[
              {
                accessor: 'contact_msisdn',
                title: byEmail ? t('broadcasts.email.recipientColumn') : t('contacts.columns.msisdn'),
                // An email broadcast's roll is addresses (4 October 2026).
                render: (r) => <code>{r.contact_msisdn ?? r.contact_email ?? ''}</code>
              },
              {
                accessor: 'status_code',
                title: t('common.status'),
                render: (r) => <RecipientStatusBadge code={r.status_code} t={t} size="sm" />
              },
              {
                accessor: 'error',
                title: t('broadcasts.view.recipients.reason'),
                // The whole point of the tab. `error_code` is the worker's
                // machine-readable reason and is translated; the sentence
                // channel-wa wrote stays in the tooltip and is the fallback
                // for a code the catalogue does not know yet. A generic
                // "failed" is exactly what this replaces.
                render: (r) => {
                  const reason = recipientReason(r, t, payer);
                  if (!reason) return <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>;
                  return (
                    <Stack gap={2}>
                      <Tooltip label={r.error ?? reason} position="top">
                        <span className="rr-broadcasts__reason">{reason}</span>
                      </Tooltip>
                      {r.attempts > 1 && (
                        <Text role="caption" c="var(--rr-color-text-muted)">
                          {t('broadcasts.view.recipients.attempts', { count: r.attempts })}
                        </Text>
                      )}
                    </Stack>
                  );
                }
              },
              {
                accessor: 'sent_at',
                title: t('status.sent'),
                render: (r) => r.sent_at ? formatDateTime(r.sent_at) : <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>
              },
              {
                accessor: 'delivered_at',
                title: t('inbox.message.delivered'),
                render: (r) => r.delivered_at ? formatDateTime(r.delivered_at) : <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>
              },
              // When they read it: WhatsApp's blue ticks (4 October 2026). SMS and email do not report reading.
              ...(whatsapp ? [{
                accessor: 'read_at' as const,
                title: t('broadcasts.recipient.read'),
                render: (r: Indexed<broadcasts.BroadcastRecipient>) => r.read_at
                  ? formatDateTime(r.read_at)
                  : <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>
              }] : [])
            ]}
          />
          <div className="rr-broadcasts__reciptotal">
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('contacts.shownOf', { count: rows.length, total: formatCount(total) })}
            </Text>
          </div>
        </Card>
      )}
    </Stack>
  );
}

/**
 * What to show as the reason: the translated code when there is one (Meta's numbered errors and codes the catalogue
 * does not know yet read as a sentence, never as a bare code), else the sentence the service wrote, else nothing.
 */
function recipientReason(
  r: Pick<broadcasts.BroadcastRecipient, 'error' | 'error_code'>,
  t: ReturnType<typeof useT>,
  payer?: 'customer'
): string | null {
  if (r.error_code) return reasonText(t, r.error_code, r.error, payer);
  return r.error ?? null;
}
